import ThisBound from 'lib/object/this-bound';
import Events from 'lib/events';
import mobile_friendly_resize_callback from 'lib/mobile-friendly-resize';
import {
    function_delay,
    set_styles,
    get_styles,
    StyleObject, remove_styles
} from 'lib/utilities';

interface Options {
    selector: string;
}

export default class MobileFriendlyHeight extends ThisBound {
    private readonly options: Options;

    private readonly events: Events = new Events();

    private readonly mobileFriendlyResizeHandler = mobile_friendly_resize_callback(this.triggerResize);

    private readonly resizeDelayed = function_delay(250, this.resize);

    private elements: HTMLElement[];

    private readonly originalStyles: Map<HTMLElement, StyleObject> = new Map;

    constructor(opts: Partial<Options> = {}) {
        super();

        this.options = MobileFriendlyHeight.resolveOptions(opts);
        this.elements = this.findElements();

        this.addEvents();
        this.triggerResize();
    }

    private findElements() {
        return Array.from(document.querySelectorAll<HTMLElement>(this.options.selector));
    }

    private addEvents() {
        this.events.on(window, 'resize', this.mobileFriendlyResizeHandler);
    }

    private triggerResize() {
        this.resize();
        this.resizeDelayed();
    }

    updateElements() {
        this.elements = this.findElements();
    }

    resize() {
        this.elements.forEach(this.resetElement);
        this.elements.forEach(this.resizeElement);
    }

    resetElement(element: HTMLElement) {
        if (this.originalStyles.has(element)) {
            remove_styles(element, ['minHeight', 'maxHeight', 'height']);

            set_styles(element, this.originalStyles.get(element));
        }
    }

    resizeElement(element: HTMLElement) {
        const height = element.offsetHeight;

        this.originalStyles.set(element, get_styles(element, ['minHeight', 'maxHeight', 'height']));

        set_styles(element, {
            height: height + 'px',
            minHeight: height + 'px',
            maxHeight: height + 'px'
        }, 'important');
    }

    destroy() {
        this.events.offAll();

        for (const [element, styles] of this.originalStyles.entries()) {
            set_styles(element, styles);
        }
    }

    static resolveOptions(opts: Partial<Options>) {
        return {
            selector: '.mobile-friendly-height',
            ...opts
        }
    }
}